import cloneDeep from 'lodash/cloneDeep'
import Swal from 'sweetalert2'

import { deepSetObject, digObject, stringToTitleCase } from '@domain-group/javascript-utils'

import type { UseFormPayload } from '@domain-group/react-hooks'

import api from '@functions/api'
import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { handleError } from '@redux/utils'
import type { DeleteEntity, EntityOptions } from '@redux/modules/types'

type BaseEntity = {
  id?: number,
}

export const mergeAndStringifyEntityObjects = <Entity extends BaseEntity, EntityParams>(
  entity: Entity,
  entityParams: EntityParams,
  entityObjectKeys: (keyof Entity | keyof EntityParams)[] = [],
  entityArrayKeys: (keyof Entity | keyof EntityParams)[] = [],
): Entity & EntityParams => {
  const updatedEntityParams = {
    id: entity.id,
    ...entityParams,
  }

  entityObjectKeys.forEach((key) => {
    // @ts-expect-error TS2536
    const entityObject = entity[key] || {}
    // @ts-expect-error TS2536
    const entityParamsObject = entityParams[key] || {}

    // @ts-expect-error TS2536
    updatedEntityParams[key] = JSON.stringify({
      ...cloneDeep(entityObject),
      ...entityParamsObject,
    })
  })

  entityArrayKeys.forEach((key) => {
    // @ts-expect-error TS2536
    const entityParamsArray = entityParams[key] || []
    // @ts-expect-error TS2536
    updatedEntityParams[key] = JSON.stringify(entityParamsArray)
  })

  // @ts-expect-error TS2322
  return updatedEntityParams
}

export const getRealhubLoginParams = () => ({
  origin_app_name: 'realhub',
  origin_redirect_url: window.location.href,
})

export function loginAsEntityUser(
  entityType: 'agencies' | 'brands' | 'providers' | 'users',
  entityId: number,
  options: EntityOptions,
) {
  const loginParams = getRealhubLoginParams()

  return () => {
    const promise = api(`/${entityType}/${entityId}/login_as.json`, { ...options, ...loginParams })
      .then(({ data }: { data: object }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}

const toggleStringValue = (existingValue: 'true' | 'false'): string => {
  const existingBoolValue = existingValue === 'true'
  const updatedValue = !existingBoolValue

  return updatedValue.toString()
}

type ToggleNestedValueOptions = {
  isString?: boolean,
  shouldSetState?: boolean,
}

// Only entityState is required
type EntityForm = Partial<UseFormPayload> & { entityState: UseFormPayload['entityState'] }

export function toggleNestedValue(entityForm: EntityForm, name: string, options: ToggleNestedValueOptions = {}) {
  const { isString, shouldSetState } = options

  // eg name = 'options.disable_preflight'
  const [column, ...keys] = name.split('.')

  const { entityState, setEntityState } = entityForm

  const existingObject = entityState[column]

  const existingValue = digObject(entityState, name)

  // Some bool values are saved as strings - 'true'
  const updatedValue = isString ? toggleStringValue(existingValue) : !existingValue
  // @ts-expect-error TS2322
  const updatedObject: object = deepSetObject(existingObject, keys.join('.'), updatedValue)

  if (shouldSetState && setEntityState){
    setEntityState({ [column]: updatedObject })
  }

  return { [column]: updatedObject }
}

type SharedActionParams = {
  callbacks: {
    afterAction?: Function,
    buildActionParams?: () => object[],
  },
  options?: {
    confirmButtonColor?: string,
    entityParams?: HandleCallbackActionParams['entityParams'],
    icon?: 'success' | 'warning',
    isBulkAction?: boolean,
    requestOptions?: HandleCallbackActionParams['requestOptions'],
    shouldRedirect?: HandleCallbackActionParams['shouldRedirect'],
  },
  strings: {
    actionString?: string,
    confirmString?: string,
    entityName: string,
    toastText?: string,
  },
}

export type ConfirmActionParams = SharedActionParams & {
  callbacks: {
    actionFn: DeleteEntity | ((params?: any) => Promise<any>),
  },
}

export const confirmAction = (params: ConfirmActionParams) => {
  const { callbacks, options, strings } = params

  const {
    actionFn, ...restCallbacks
  } = callbacks
  const {
    actionString,
    confirmString = 'Are you sure?',
    entityName,
    toastText,
  } = strings

  const {
    confirmButtonColor = '#43AC6A',
    entityParams,
    icon = 'success',
    isBulkAction = false,
    requestOptions,
    shouldRedirect,
  } = options || {}

  const themOrIt = () => {
    if (isBulkAction) return 'them'
    if (entityName === 'User') return 'them'

    return 'it'
  }

  const title = `${actionString} ${entityName}?` // Delete Agency?
  const formattedToastText = toastText || `${entityName} ${actionString}d` // Agency Deleted
  const confirmButtonText = `Yes, ${actionString} ${themOrIt()}.` // Yes, delete it.

  const payload = {
    callbacks: {
      action: actionFn,
      ...restCallbacks,
    },
    entityParams,
    requestOptions,
    shouldRedirect,
    toastText: stringToTitleCase(formattedToastText),
  }

  Swal
    .fire({
      confirmButtonColor,
      confirmButtonText,
      icon,
      showCancelButton: true,
      text: confirmString,
      title: stringToTitleCase(title),
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        handleCallbackAction(payload)
      }
    })
}

type ConfirmDeleteParams = SharedActionParams & {
  callbacks: {
    deleteFn: DeleteEntity,
  },
  options?: {
    isBulkDelete?: boolean,
  },
  strings: {
    deleteString?: 'archive' | 'delete' | 'remove' | 'unapprove' | 'unassign',
  },
}

export const confirmDelete = (params: ConfirmDeleteParams) => {
  const { callbacks, options, strings } = params

  confirmAction({
    callbacks: {
      ...callbacks,
      actionFn: callbacks.deleteFn,
    },
    options: {
      ...options,
      confirmButtonColor: '#DD6B55',
      icon: 'warning',
      isBulkAction: options?.isBulkDelete,
    },
    strings: {
      ...strings,
      actionString: strings?.deleteString || 'delete',
    },
  })
}

export default mergeAndStringifyEntityObjects

export const mapEntityTypeToEntityKey = (entityType: 'Agency' | 'Brand' | 'Provider') => {
  const entityKeys = {
    Agency: 'agencies',
    Brand: 'brands',
    Provider: 'providers',
  }

  return entityKeys[entityType] || ''
}

type FormatEntityCountParams = {
  entityString: string,
  entityStringPlural?: string,
  entityCount: number,
}

export const formatEntityCount = (params: FormatEntityCountParams) : string => {
  const { entityString, entityStringPlural, entityCount } = params

  if (entityCount === 1) return `${entityCount} ${entityString}`

  return `${entityCount} ${entityStringPlural || `${entityString}s`}`
}

export const sanitizeFilters = (filters: object) => {
  const updatedFilters = { ...filters }

  Object.keys(updatedFilters).forEach((key) => {
    // @ts-expect-error TS7053
    if (updatedFilters[key] === 'all'){
      // @ts-expect-error TS7053
      updatedFilters[key] = ''
    }
  })

  return updatedFilters
}
